import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { updatePassword, updateProfile } from '../../services/api';

const ProfileSettings = ({ user, onBack, onUserUpdate }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [profileData, setProfileData] = useState({
    email: user?.email || '',
    full_name: user?.full_name || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleCopyShareId = async () => {
    try {
      await navigator.clipboard.writeText(user.share_id);
      toast.success('Share ID copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy Share ID');
    }
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(user.email);
      toast.success('Email copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy email');
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');

    if (!profileData.email) {
      setProfileError('Email is required');
      return;
    }

    setIsSavingProfile(true);
    try {
      const result = await updateProfile(profileData.email, profileData.full_name);
      if (result.success) {
        toast.success('Profile updated successfully');
        setIsEditingProfile(false);
        // Update the user data in parent component if callback provided
        if (onUserUpdate && result.data) {
          onUserUpdate(result.data);
        }
      } else {
        setProfileError(result.error || 'Failed to update profile');
      }
    } catch (error) {
      setProfileError('Failed to update profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    setProfileError('');
    setProfileData({
      email: user?.email || '',
      full_name: user?.full_name || ''
    });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    setIsChangingPassword(true);
    try {
      const result = await updatePassword(passwordData.currentPassword, passwordData.newPassword);
      if (result.success) {
        toast.success('Password updated successfully');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setPasswordError(result.error || 'Failed to update password');
      }
    } catch (error) {
      setPasswordError('Failed to update password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (!user) {
    return (
      <div className="flex-1 flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-border-primary border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-bg-primary">
      {/* Header */}
      <div className="pd-header">
        <button onClick={onBack} className="btn-back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back
        </button>
        
        <div className="pd-header-content">
          <div className="pd-title-row">
            <h2 className="pd-title">Profile Settings</h2>
          </div>
          <div className="pd-metadata">
            <span className="pd-meta-item">
              <span className="pd-meta-label">Member since:</span>
              <span className="pd-meta-value">
                {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { 
                  month: 'long', 
                  year: 'numeric' 
                }) : 'N/A'}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="pd-tabs">
        <button
          className={`pd-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          className={`pd-tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
      </div>

      {/* Content */}
      <div className="pd-content">
        {activeTab === 'profile' && (
          <div className="pd-input-section">
            {/* Profile Card */}
            <div className="pd-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0 bg-gradient-to-br from-primary-600 to-primary-500 shadow-glow-sm text-black">
                    {(user.username || user.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">{user.username}</h3>
                    <p className="text-xs text-text-tertiary">Username cannot be changed</p>
                  </div>
                </div>
                {!isEditingProfile && (
                  <button
                    onClick={() => setIsEditingProfile(true)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 bg-success-50 border border-primary-500/20 text-primary-500 hover:bg-success-100"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    Edit
                  </button>
                )}
              </div>

              {isEditingProfile ? (
                <form onSubmit={handleProfileSave} className="space-y-4">
                  {profileError && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm bg-error-bg border border-error/30 text-red-400">
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      <span>{profileError}</span>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => {
                        setProfileData(prev => ({ ...prev, email: e.target.value }));
                        if (profileError) setProfileError('');
                      }}
                      className="w-full px-4 py-2.5 text-sm bg-bg-input border border-border-primary rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-text-primary"
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">
                      Full Name <span className="text-text-tertiary">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={profileData.full_name}
                      onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                      className="w-full px-4 py-2.5 text-sm bg-bg-input border border-border-primary rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-text-primary"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={isSavingProfile}
                      className="px-4 py-2 text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-primary-500 text-black hover:bg-primary-600"
                    >
                      {isSavingProfile ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 text-sm font-medium rounded-lg transition-all bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-3 pt-2 border-t border-white/5">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-text-tertiary">Email</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-text-primary">{user.email}</span>
                      <button
                        onClick={handleCopyEmail}
                        className="p-1 text-text-tertiary hover:text-primary-500 rounded transition-all"
                        title="Copy email"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-text-tertiary">Full Name</span>
                    <span className="text-sm text-text-primary">{user.full_name || '—'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Share ID Card */}
            {user.share_id && (
              <div className="pd-card p-6">
                <h3 className="pd-section-title">Your Share ID</h3>
                <p className="text-xs text-text-tertiary mb-3">
                  Share this ID with workspace admins to get invited to their workspaces
                </p>
                <div className="flex items-center justify-between p-4 rounded-lg bg-success-50 border border-primary-500/20">
                  <code className="text-2xl font-bold tracking-[0.3em] text-primary-500">
                    {user.share_id}
                  </code>
                  <button
                    onClick={handleCopyShareId}
                    className="p-2 rounded-lg transition-all bg-success-100 text-primary-500 hover:bg-primary-500 hover:text-black"
                    title="Copy Share ID"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Account Info */}
            <div className="pd-card p-6">
              <h3 className="pd-section-title">Account Information</h3>
              <div className="pd-grid mt-4">
                <div>
                  <span className="text-xs text-text-tertiary">User ID</span>
                  <div className="text-sm text-text-secondary font-mono mt-1">
                    {user.user_id ? `${user.user_id.substring(0, 8)}...` : 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-text-tertiary">Account Created</span>
                  <div className="text-sm text-text-secondary mt-1">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-text-tertiary">Account Status</span>
                  <div className="mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-success-50 text-primary-500">
                      Active
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="pd-input-section">
            {/* Change Password */}
            <div className="pd-card p-6">
              <h3 className="pd-section-title">Change Password</h3>
              <p className="text-xs text-text-tertiary mb-4">
                Update your password to keep your account secure
              </p>
              
              <form onSubmit={handlePasswordChange} className="space-y-4">
                {passwordError && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm bg-error-bg border border-error/30 text-red-400">
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{passwordError}</span>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => {
                      setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }));
                      if (passwordError) setPasswordError('');
                    }}
                    className="w-full px-4 py-2.5 text-sm bg-bg-input border border-border-primary rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-text-primary"
                    placeholder="Enter current password"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => {
                      setPasswordData(prev => ({ ...prev, newPassword: e.target.value }));
                      if (passwordError) setPasswordError('');
                    }}
                    className="w-full px-4 py-2.5 text-sm bg-bg-input border border-border-primary rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-text-primary"
                    placeholder="Enter new password"
                    required
                    minLength={6}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => {
                      setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }));
                      if (passwordError) setPasswordError('');
                    }}
                    className="w-full px-4 py-2.5 text-sm bg-bg-input border border-border-primary rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-text-primary"
                    placeholder="Confirm new password"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="px-4 py-2.5 text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-primary-500 text-black hover:bg-primary-600"
                >
                  {isChangingPassword ? (
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </span>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </form>
            </div>

            {/* Security Tips */}
            <div className="pd-card p-6">
              <h3 className="pd-section-title">Security Tips</h3>
              <ul className="space-y-2 mt-3 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Use a strong password with at least 8 characters
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Include uppercase, lowercase, numbers, and symbols
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Don't reuse passwords from other accounts
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Never share your password with anyone
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ProfileSettings.propTypes = {
  user: PropTypes.shape({
    user_id: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    full_name: PropTypes.string,
    share_id: PropTypes.string,
    created_at: PropTypes.string
  }),
  onBack: PropTypes.func.isRequired,
  onUserUpdate: PropTypes.func
};

export default ProfileSettings;
